import { z } from "zod";

/**
 * A new WhatsApp template, as the panel writes it (f6-08): a text header, a body with
 * {{1}}, {{2}}…, a footer and simple buttons. Shared so the panel shows the same problems
 * the API refuses, before Meta does. Images, carousels and buttons with variables: later.
 */

export const TEMPLATE_LANGUAGES = {
  es: "Español",
  es_MX: "Español (México)",
  es_ES: "Español (España)",
  en_US: "Inglés (EE. UU.)",
} as const;
export type TemplateLanguage = keyof typeof TEMPLATE_LANGUAGES;

export const TEMPLATE_LIMITS = { header: 60, body: 1024, footer: 60, buttonText: 25, buttons: 10, urls: 2, phones: 1 };

const button = z.discriminatedUnion("type", [
  z.object({ type: z.literal("QUICK_REPLY"), text: z.string().trim() }),
  z.object({ type: z.literal("URL"), text: z.string().trim(), url: z.string().trim() }),
  z.object({ type: z.literal("PHONE_NUMBER"), text: z.string().trim(), phone: z.string().trim() }),
]);

export const templateDraftSchema = z.object({
  /** Which WhatsApp Business Account, when the company has more than one. */
  wabaId: z.string().regex(/^\d+$/).optional(),
  name: z.string().trim(),
  language: z.enum(Object.keys(TEMPLATE_LANGUAGES) as [TemplateLanguage, ...TemplateLanguage[]]),
  category: z.enum(["MARKETING", "UTILITY"]),
  headerText: z.string().trim().nullable().optional(),
  headerExample: z.string().trim().nullable().optional(),
  bodyText: z.string().trim(),
  bodyExamples: z.array(z.string().trim()).max(20).default([]),
  footerText: z.string().trim().nullable().optional(),
  buttons: z.array(button).max(20).default([]),
});
export type TemplateDraftInput = z.infer<typeof templateDraftSchema>;

const VARIABLE = /\{\{\s*([^}]*?)\s*\}\}/g;
const variablesIn = (text: string) => [...text.matchAll(VARIABLE)].map((m) => m[1] ?? "");

/** "Recordatorio de cita" -> "recordatorio_de_cita": the name Meta accepts. */
export function templateNameFrom(label: string): string {
  return label
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 512);
}

/** How many {{n}} the body uses (n must go 1, 2, 3… in order). */
export const bodyVariableCount = (body: string) => variablesIn(body).length;

/** What Meta would refuse, in words for the person writing it. Empty when it can be sent. */
export function templateDraftProblems(d: TemplateDraftInput): string[] {
  const out: string[] = [];
  if (!/^[a-z0-9_]{1,512}$/.test(d.name))
    out.push("El nombre solo puede tener minúsculas, números y guiones bajos (por ejemplo, recordatorio_de_cita).");

  if (d.headerText) {
    if (d.headerText.length > TEMPLATE_LIMITS.header)
      out.push(`El encabezado tiene más de ${TEMPLATE_LIMITS.header} caracteres.`);
    const vars = variablesIn(d.headerText);
    if (vars.length > 1 || (vars.length === 1 && vars[0] !== "1"))
      out.push("El encabezado puede tener como mucho una variable, y tiene que ser {{1}}.");
    else if (vars.length === 1 && !d.headerExample) out.push("Escribe un ejemplo para la variable del encabezado.");
    if (/[*_~\n]|\p{Extended_Pictographic}/u.test(d.headerText))
      out.push("El encabezado no puede tener saltos de línea, emojis ni formato (*, _, ~).");
  }

  if (!d.bodyText) out.push("Escribe el mensaje.");
  else {
    if (d.bodyText.length > TEMPLATE_LIMITS.body)
      out.push(`El mensaje tiene más de ${TEMPLATE_LIMITS.body} caracteres.`);
    const vars = variablesIn(d.bodyText);
    if (vars.some((v, i) => v !== String(i + 1)))
      out.push("Las variables del mensaje van numeradas en orden: {{1}}, {{2}}, {{3}}… sin saltarse ni repetir.");
    else if (vars.length) {
      if (/^\s*\{\{/.test(d.bodyText) || /\}\}[\s.!?¡¿,;:]*$/.test(d.bodyText))
        out.push("El mensaje no puede empezar ni terminar con una variable: agrega texto antes o después.");
      if (d.bodyExamples.length !== vars.length || d.bodyExamples.some((e) => !e))
        out.push("Escribe un ejemplo para cada variable del mensaje: Meta lo usa para revisarla.");
    }
  }

  if (d.footerText) {
    if (d.footerText.length > TEMPLATE_LIMITS.footer)
      out.push(`El pie tiene más de ${TEMPLATE_LIMITS.footer} caracteres.`);
    if (variablesIn(d.footerText).length) out.push("El pie no puede tener variables.");
  }

  if (d.buttons.length > TEMPLATE_LIMITS.buttons) out.push(`Como mucho ${TEMPLATE_LIMITS.buttons} botones.`);
  if (d.buttons.filter((b) => b.type === "URL").length > TEMPLATE_LIMITS.urls)
    out.push("Como mucho 2 botones de enlace.");
  if (d.buttons.filter((b) => b.type === "PHONE_NUMBER").length > TEMPLATE_LIMITS.phones)
    out.push("Como mucho 1 botón de llamada.");
  for (const b of d.buttons) {
    if (!b.text || b.text.length > TEMPLATE_LIMITS.buttonText)
      out.push(`Cada botón necesita un texto de hasta ${TEMPLATE_LIMITS.buttonText} caracteres.`);
    if (b.type === "URL" && (!/^https:\/\/\S+$/.test(b.url) || variablesIn(b.url).length))
      out.push("Los enlaces de los botones empiezan con https:// y no llevan variables.");
    if (b.type === "PHONE_NUMBER" && !/^\+\d{8,15}$/.test(b.phone.replace(/[\s-]/g, "")))
      out.push("El teléfono del botón va con código de país, por ejemplo +506 8888 7777.");
  }
  return [...new Set(out)];
}
